import React from 'react';
import { AnimatePresence } from 'framer-motion';
import type { ApiSequenceConfig } from '@chatsdet/types';
import { useApiRequestList } from '../../../features/api-catalog/api';
import { ApiSequencePreview } from './ApiSequencePreview';
import { ApiSequenceEditor } from './ApiSequenceEditor';
import {
  useApiSequence,
  useApiSequenceUpdateConfig,
} from '../../pages/FlowDesigner/api/hooks';

type ApiSequenceNodeConfigProps = {
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  propertyId?: string;
  nodeId?: string;
  /** The parent workflow's ID, used to look up linked api sequences */
  workflowId?: string;
  /** Persist config directly to the backend, bypassing the outer dirty check. */
  onSave?: (data: Record<string, any>) => void;
};

/**
 * API Sequence node configuration component.
 *
 * When an apiSequenceId is set on the node, the config is fetched from the
 * api_sequences table (single source of truth). Edits save back to that record.
 * Falls back to reading config from localData for legacy nodes.
 */
export const ApiSequenceNodeConfig: React.FC<ApiSequenceNodeConfigProps> = ({
  localData,
  setLocalData,
  propertyId,
  nodeId,
  workflowId,
  onSave,
}) => {
  const [isEditorOpen, setIsEditorOpen] = React.useState(false);

  // If the node has an apiSequenceId, fetch the canonical config from the API
  const linkedApiSequenceId = localData.apiSequenceId as string | undefined;
  const linkedWorkflowId = workflowId ?? (linkedApiSequenceId ? undefined : undefined);
  const sequenceQ = useApiSequence(linkedWorkflowId ?? null);
  const updateConfigMutation = useApiSequenceUpdateConfig(linkedWorkflowId ?? null);

  // Fetch API requests for the property
  const { data: apiRequestsData } = useApiRequestList({
    propertyId: propertyId || undefined,
    pageSize: 100,
  });
  const apiRequests = apiRequestsData?.data || [];

  // Resolve config: prefer linked api_sequence record, fall back to localData
  const config: ApiSequenceConfig = React.useMemo(() => {
    if (sequenceQ.data?.config) {
      return sequenceQ.data.config;
    }
    return {
      steps: Array.isArray(localData.steps) ? localData.steps : [],
      description: typeof localData.description === 'string' ? localData.description : undefined,
      stopOnFailure: localData.stopOnFailure !== false,
      exports: Array.isArray(localData.exports) ? localData.exports : undefined,
    };
  }, [localData, sequenceQ.data]);

  const handleSave = React.useCallback(
    (newConfig: ApiSequenceConfig) => {
      // If linked to an api_sequence record, update there
      if (sequenceQ.data?.id) {
        updateConfigMutation.mutate(
          { apiSequenceId: sequenceQ.data.id, config: newConfig },
          {
            onSuccess: () => {
              // Keep localData lightweight — just the reference + cached description
              const refData = {
                apiSequenceId: sequenceQ.data!.id,
                description: newConfig.description,
              };
              setLocalData(refData);
              setIsEditorOpen(false);
              onSave?.(refData);
            },
          }
        );
        return;
      }

      // Legacy path: store full config in localData
      const merged = { ...localData, ...newConfig };
      setLocalData(merged);
      setIsEditorOpen(false);
      onSave?.(merged);
    },
    [localData, setLocalData, onSave, sequenceQ.data, updateConfigMutation]
  );

  const handleCancel = React.useCallback(() => {
    setIsEditorOpen(false);
  }, []);

  return (
    <>
      {/* Preview in side panel */}
      <ApiSequencePreview
        config={config}
        apiRequests={apiRequests}
        onEdit={() => setIsEditorOpen(true)}
      />

      {/* Full-screen editor overlay */}
      <AnimatePresence>
        {isEditorOpen && (
          <ApiSequenceEditor
            isOpen={isEditorOpen}
            config={config}
            apiRequests={apiRequests}
            propertyId={propertyId}
            nodeId={nodeId}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}
      </AnimatePresence>
    </>
  );
};
